import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken, Point, Rewards } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { useMemo } from 'react';
import { calculateProjectedEarningsMonthly, calculateProjectedEarningsYearly } from '@/helpers/apy';
import { useVaultV1Apy, type VaultV1ApyInput } from '@/hooks/useVaultV1Apy';
import type { VaultV1ActionPanelData } from '../../vaultV1ActionPanelData';
interface UseVaultV1SummaryDataProps {
  vaultData: VaultV1ActionPanelData | null | undefined;
  vaultAsset: InterfaceToken | null | undefined;
  initialPosition: bigint | undefined;
  finalPosition: bigint | undefined;
}
export interface VaultV1SummaryData {
  apy: {
    raw?: bigint | null;
    net?: bigint | null;
    formatted: string;
    performanceFee?: bigint | null;
    points: Point[];
    rewards: Rewards[];
  };
  position: {
    initial: {
      raw?: bigint;
      formatted: string;
      detailed?: string;
    };
    final: {
      raw?: bigint;
      formatted?: string;
      detailed?: string;
    };
    showFinalValue: boolean;
  };
  projectedEarnings: {
    yearly: {
      initial: string;
      initialTooltip?: string;
      final?: string;
      finalTooltip?: string;
      showFinalValue: boolean;
    };
    monthly: {
      initial: string;
      initialTooltip?: string;
      final?: string;
      finalTooltip?: string;
      showFinalValue: boolean;
    };
  };
}
export const useVaultV1SummaryData = t0 => {
  const $ = _c(109);
  const {
    vaultData,
    vaultAsset,
    initialPosition,
    finalPosition
  } = t0;
  const vaultV1 = vaultData;
  const chainId = vaultAsset?.chain.id;
  let t1;
  bb0: {
    if (!vaultV1 || !chainId) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[0] !== vaultV1.address || $[1] !== vaultV1.asset || $[2] !== vaultV1.rewards || $[3] !== vaultV1.state) {
      t2 = {
        address: vaultV1.address,
        asset: vaultV1.asset,
        state: vaultV1.state,
        rewards: vaultV1.rewards
      };
      $[0] = vaultV1.address;
      $[1] = vaultV1.asset;
      $[2] = vaultV1.rewards;
      $[3] = vaultV1.state;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  const apyInput = t1;
  let t2;
  if ($[5] !== apyInput) {
    t2 = {
      vaultV1: apyInput
    };
    $[5] = apyInput;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    apy,
    netApy,
    performanceFee,
    points,
    rewards
  } = useVaultV1Apy(t2);
  let t3;
  if ($[7] !== initialPosition || $[8] !== vaultAsset?.decimals || $[9] !== vaultAsset?.price) {
    t3 = () => formatters.short.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, initialPosition)).trailingZero(!initialPosition).default("0.00").of(initialPosition, vaultAsset?.decimals);
    $[7] = initialPosition;
    $[8] = vaultAsset?.decimals;
    $[9] = vaultAsset?.price;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  vaultAsset?.decimals;
  vaultAsset?.price;
  let t4;
  if ($[11] !== t3) {
    t4 = t3();
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const formattedInitialPosition = t4;
  vaultAsset?.decimals;
  vaultAsset?.price;
  vaultAsset?.symbol;
  let t5;
  if ($[13] !== initialPosition || $[14] !== vaultAsset?.decimals || $[15] !== vaultAsset?.price || $[16] !== vaultAsset?.symbol) {
    t5 = initialPosition && initialPosition > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, initialPosition)).trailingZero(false).unit(vaultAsset?.symbol).of(initialPosition, vaultAsset?.decimals) : undefined;
    $[13] = initialPosition;
    $[14] = vaultAsset?.decimals;
    $[15] = vaultAsset?.price;
    $[16] = vaultAsset?.symbol;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const formattedInitialPositionDetailed = t5;
  let t6;
  if ($[18] !== finalPosition || $[19] !== vaultAsset?.decimals || $[20] !== vaultAsset?.price) {
    t6 = () => formatters.short.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, finalPosition)).trailingZero(!finalPosition).of(finalPosition, vaultAsset?.decimals);
    $[18] = finalPosition;
    $[19] = vaultAsset?.decimals;
    $[20] = vaultAsset?.price;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  vaultAsset?.decimals;
  vaultAsset?.price;
  let t7;
  if ($[22] !== t6) {
    t7 = t6();
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const formattedFinalPosition = t7;
  vaultAsset?.decimals;
  vaultAsset?.price;
  vaultAsset?.symbol;
  let t8;
  if ($[24] !== finalPosition || $[25] !== vaultAsset?.decimals || $[26] !== vaultAsset?.price || $[27] !== vaultAsset?.symbol) {
    t8 = finalPosition && finalPosition > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, finalPosition)).trailingZero(false).unit(vaultAsset?.symbol).of(finalPosition, vaultAsset?.decimals) : undefined;
    $[24] = finalPosition;
    $[25] = vaultAsset?.decimals;
    $[26] = vaultAsset?.price;
    $[27] = vaultAsset?.symbol;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const formattedFinalPositionDetailed = t8;
  let t9;
  if ($[29] !== netApy) {
    t9 = formatters.percent.digits(2).default("-").unit("%").max(10).of(netApy, 18);
    $[29] = netApy;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const formattedNetApy = t9;
  let t10;
  if ($[31] !== initialPosition || $[32] !== netApy || $[33] !== vaultAsset) {
    t10 = formatters.short.digits(2).default("$0.00").unit("$").max(100000000).of(calculateProjectedEarningsYearly(netApy, initialPosition, vaultAsset), 18);
    $[31] = initialPosition;
    $[32] = netApy;
    $[33] = vaultAsset;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const formattedYearlyInitial = t10;
  let t11;
  if ($[35] !== initialPosition || $[36] !== netApy || $[37] !== vaultAsset) {
    t11 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(calculateProjectedEarningsYearly(netApy, initialPosition, vaultAsset), 18);
    $[35] = initialPosition;
    $[36] = netApy;
    $[37] = vaultAsset;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const detailedYearlyInitial = t11;
  let t12;
  if ($[39] !== finalPosition || $[40] !== netApy || $[41] !== vaultAsset) {
    t12 = formatters.short.digits(2).unit("$").max(100000000).of(calculateProjectedEarningsYearly(netApy, finalPosition, vaultAsset), 18);
    $[39] = finalPosition;
    $[40] = netApy;
    $[41] = vaultAsset;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const formattedYearlyFinal = t12;
  let t13;
  if ($[43] !== finalPosition || $[44] !== netApy || $[45] !== vaultAsset) {
    t13 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(calculateProjectedEarningsYearly(netApy, finalPosition, vaultAsset), 18);
    $[43] = finalPosition;
    $[44] = netApy;
    $[45] = vaultAsset;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const detailedYearlyFinal = t13;
  let t14;
  if ($[47] !== initialPosition || $[48] !== netApy || $[49] !== vaultAsset) {
    t14 = formatters.short.digits(2).default("$0.00").unit("$").max(100000000).of(calculateProjectedEarningsMonthly(netApy, initialPosition, vaultAsset), 18);
    $[47] = initialPosition;
    $[48] = netApy;
    $[49] = vaultAsset;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const formattedMonthlyInitial = t14;
  let t15;
  if ($[51] !== initialPosition || $[52] !== netApy || $[53] !== vaultAsset) {
    t15 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(calculateProjectedEarningsMonthly(netApy, initialPosition, vaultAsset), 18);
    $[51] = initialPosition;
    $[52] = netApy;
    $[53] = vaultAsset;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const detailedMonthlyInitial = t15;
  let t16;
  if ($[55] !== finalPosition || $[56] !== netApy || $[57] !== vaultAsset) {
    t16 = formatters.short.digits(2).unit("$").max(100000000).of(calculateProjectedEarningsMonthly(netApy, finalPosition, vaultAsset), 18);
    $[55] = finalPosition;
    $[56] = netApy;
    $[57] = vaultAsset;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  const formattedMonthlyFinal = t16;
  let t17;
  if ($[59] !== finalPosition || $[60] !== netApy || $[61] !== vaultAsset) {
    t17 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(calculateProjectedEarningsMonthly(netApy, finalPosition, vaultAsset), 18);
    $[59] = finalPosition;
    $[60] = netApy;
    $[61] = vaultAsset;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const detailedMonthlyFinal = t17;
  let t18;
  if ($[63] !== finalPosition) {
    t18 = isDefined(finalPosition);
    $[63] = finalPosition;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  const showFinalValue = t18;
  let t19;
  if ($[65] !== formattedYearlyFinal || $[66] !== showFinalValue) {
    t19 = showFinalValue && isDefined(formattedYearlyFinal);
    $[65] = formattedYearlyFinal;
    $[66] = showFinalValue;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  const showYearlyEarningsFinalValue = t19;
  let t20;
  if ($[68] !== formattedMonthlyFinal || $[69] !== showFinalValue) {
    t20 = showFinalValue && isDefined(formattedMonthlyFinal);
    $[68] = formattedMonthlyFinal;
    $[69] = showFinalValue;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  const showMonthlyEarningsFinalValue = t20;
  let t21;
  if ($[71] !== apy || $[72] !== formattedNetApy || $[73] !== netApy || $[74] !== performanceFee || $[75] !== points || $[76] !== rewards) {
    t21 = {
      raw: apy,
      net: netApy,
      formatted: formattedNetApy,
      performanceFee,
      points,
      rewards
    };
    $[71] = apy;
    $[72] = formattedNetApy;
    $[73] = netApy;
    $[74] = performanceFee;
    $[75] = points;
    $[76] = rewards;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] !== formattedInitialPosition || $[79] !== formattedInitialPositionDetailed || $[80] !== initialPosition) {
    t22 = {
      raw: initialPosition,
      formatted: formattedInitialPosition,
      detailed: formattedInitialPositionDetailed
    };
    $[78] = formattedInitialPosition;
    $[79] = formattedInitialPositionDetailed;
    $[80] = initialPosition;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] !== finalPosition || $[83] !== formattedFinalPosition || $[84] !== formattedFinalPositionDetailed) {
    t23 = {
      raw: finalPosition,
      formatted: formattedFinalPosition,
      detailed: formattedFinalPositionDetailed
    };
    $[82] = finalPosition;
    $[83] = formattedFinalPosition;
    $[84] = formattedFinalPositionDetailed;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== showFinalValue || $[87] !== t22 || $[88] !== t23) {
    t24 = {
      initial: t22,
      final: t23,
      showFinalValue
    };
    $[86] = showFinalValue;
    $[87] = t22;
    $[88] = t23;
    $[89] = t24;
  } else {
    t24 = $[89];
  }
  let t25;
  if ($[90] !== detailedYearlyFinal || $[91] !== detailedYearlyInitial || $[92] !== formattedYearlyFinal || $[93] !== formattedYearlyInitial || $[94] !== showYearlyEarningsFinalValue) {
    t25 = {
      initial: formattedYearlyInitial,
      initialTooltip: detailedYearlyInitial,
      final: formattedYearlyFinal,
      finalTooltip: detailedYearlyFinal,
      showFinalValue: showYearlyEarningsFinalValue
    };
    $[90] = detailedYearlyFinal;
    $[91] = detailedYearlyInitial;
    $[92] = formattedYearlyFinal;
    $[93] = formattedYearlyInitial;
    $[94] = showYearlyEarningsFinalValue;
    $[95] = t25;
  } else {
    t25 = $[95];
  }
  let t26;
  if ($[96] !== detailedMonthlyFinal || $[97] !== detailedMonthlyInitial || $[98] !== formattedMonthlyFinal || $[99] !== formattedMonthlyInitial || $[100] !== showMonthlyEarningsFinalValue) {
    t26 = {
      initial: formattedMonthlyInitial,
      initialTooltip: detailedMonthlyInitial,
      final: formattedMonthlyFinal,
      finalTooltip: detailedMonthlyFinal,
      showFinalValue: showMonthlyEarningsFinalValue
    };
    $[96] = detailedMonthlyFinal;
    $[97] = detailedMonthlyInitial;
    $[98] = formattedMonthlyFinal;
    $[99] = formattedMonthlyInitial;
    $[100] = showMonthlyEarningsFinalValue;
    $[101] = t26;
  } else {
    t26 = $[101];
  }
  let t27;
  if ($[102] !== t25 || $[103] !== t26) {
    t27 = {
      yearly: t25,
      monthly: t26
    };
    $[102] = t25;
    $[103] = t26;
    $[104] = t27;
  } else {
    t27 = $[104];
  }
  let t28;
  if ($[105] !== t21 || $[106] !== t24 || $[107] !== t27) {
    t28 = {
      apy: t21,
      position: t24,
      projectedEarnings: t27
    };
    $[105] = t21;
    $[106] = t24;
    $[107] = t27;
    $[108] = t28;
  } else {
    t28 = $[108];
  }
  return t28;
};